import { useComplianceNotificationFlagGates } from '@/app-shell/flags/meddevice-compliance-notification-flags';
import { useQueryNotifications } from '@/hooks/query/useQueryNotifications';
import {
  type PinnedComplianceNotification,
  useReadComplianceNotifications,
} from '@heidi/growth';
import { useEffectiveSessionTimeout } from '@heidi/workspace/data';
import { useMemo } from 'react';

/**
 * One row of the notification list. Inmails are addressed by their index into
 * the query result rather than by value, because `NotificationCard` reads the
 * notification out of the cache itself — merging by value would mean rewriting
 * that component's contract for a change it has no stake in.
 */
export type NotificationListItem =
  | { kind: 'inmail'; key: string; inmailIndex: number }
  | { kind: 'compliance'; key: string; notification: PinnedComplianceNotification };

/** Missing or malformed stamps sort oldest, so a bad row can't outrank real ones. */
const toTime = (value: string | null | undefined): number => {
  if (!value) return 0;
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? 0 : parsed;
};

/**
 * The notification list, with read compliance recommendations merged into it by
 * time.
 *
 * The inmails keep the order the backend sent them in — compliance rows are
 * INSERTED into that sequence, never sorted alongside it. A client-side sort on
 * `created_at` would look equivalent today and silently reorder every row the
 * day the backend orders on anything else.
 *
 * Unread recommendations are absent here: they are pinned above the list by
 * `PinnedComplianceNotificationsSection`.
 */
export function useNotificationListItems({
  includeInmails,
}: {
  includeInmails: boolean;
}): NotificationListItem[] {
  const { data: inmails } = useQueryNotifications();
  // Read here rather than taken as a parameter: this hook is the list's own
  // data source and `NotificationsPanel` — its only caller — already subscribes
  // to the same hook for the pinned half. `useEffectiveSessionTimeout` reads one
  // selected field off the shared `GET /user` query, so a second call is a
  // second selector over the same cache entry, not a second request. `?? null`
  // collapses the hook's `| undefined` arm, which
  // `resolveEffectiveSessionTimeout` declares but cannot return.
  const effectiveSessionTimeout = useEffectiveSessionTimeout() ?? null;
  const flagGates = useComplianceNotificationFlagGates();
  const readCompliance = useReadComplianceNotifications(
    effectiveSessionTimeout,
    flagGates
  );

  return useMemo(() => {
    const rows: Array<{ item: NotificationListItem; at: number }> =
      includeInmails && inmails
        ? inmails.map((inmail, inmailIndex) => ({
            item: {
              kind: 'inmail' as const,
              key: inmail.id ?? `inmail-${inmailIndex}`,
              inmailIndex,
            },
            at: toTime(inmail.created_at),
          }))
        : [];

    for (const notification of readCompliance) {
      const at = toTime(notification.firstShownAt);
      const row = {
        item: {
          kind: 'compliance' as const,
          key: `compliance-${notification.type}`,
          notification,
        },
        at,
      };
      const olderRowIndex = rows.findIndex((existing) => existing.at < at);
      if (olderRowIndex === -1) {
        rows.push(row);
      } else {
        rows.splice(olderRowIndex, 0, row);
      }
    }

    return rows.map(({ item }) => item);
  }, [includeInmails, inmails, readCompliance]);
}
